'use client';

import { c as _c } from "react/compiler-runtime";
import { prependOptimisticThesis, useActiveWallet, useAddThesis, useGetMarket, useGetMe, useQueryClient } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TextareaField } from '@/components/ui/textarea-field';
import { outcomeHueToStepVar, useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useFormattedCurrency } from '@/lib/currency';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { buildThesisShareUrl, ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
import { useLocalThesesStore } from '@/stores/local-theses';
import { type ThesisTradeContext, useThesisDialogStore } from '@/stores/thesis-dialog';
const MAX_BYTES = 280;
type DialogView = 'input' | 'success';
interface ResolvedOutcome {
  label: string | null;
  imageUrl: string | undefined;
}
export function ThesisDialog() {
  const $ = _c(61);
  const t = useTranslations("thesis");
  const {
    open,
    marketAddress,
    tradeSignature,
    onChainSignature,
    tradeContext,
    closeDialog
  } = useThesisDialogStore();
  const [content, setContent] = useState("");
  const [view, setView] = useState("input");
  const [submittedSignature, setSubmittedSignature] = useState(null);
  const thesisMutation = useAddThesis();
  const {
    activeWallet
  } = useActiveWallet();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fields: ["id", "displayName", "handle", "avatarUrl", "avatarNsfw", "avatarPreferences"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const meQuery = useGetMe(t0);
  const t1 = marketAddress ?? "";
  let t2;
  if ($[1] !== t1) {
    t2 = {
      pubkey: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const marketQuery = useGetMarket(t2);
  let t3;
  bb0: {
    if (tradeContext === null) {
      t3 = {
        label: null,
        imageUrl: undefined
      };
      break bb0;
    }
    const outcomes = marketQuery.data?.outcomes;
    if (outcomes !== undefined) {
      let t4;
      if ($[3] !== tradeContext) {
        t4 = o => Number(o.outcomeIndex) === tradeContext.outcomeIndex;
        $[3] = tradeContext;
        $[4] = t4;
      } else {
        t4 = $[4];
      }
      const match = outcomes.find(t4);
      if (match !== undefined) {
        const matchLabel = match.label;
        const matchImageUrl = (match as {
          imageUrl?: string;
        }).imageUrl;
        t3 = {
          label: matchLabel !== undefined && matchLabel.length > 0 ? matchLabel : tradeContext.outcomeLabel,
          imageUrl: matchImageUrl !== undefined && matchImageUrl.length > 0 ? matchImageUrl : undefined
        };
        break bb0;
      }
    }
    t3 = {
      label: tradeContext.outcomeLabel,
      imageUrl: undefined
    };
  }
  const resolvedOutcome = t3;
  let t4;
  let t5;
  if ($[5] !== open) {
    t4 = () => {
      if (open) {
        setView("input");
        setSubmittedSignature(null);
      }
    };
    t5 = [open];
    $[5] = open;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== content) {
    t6 = new TextEncoder().encode(content);
    $[8] = content;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const byteLength = t6.length;
  const isDisabled = byteLength === 0 || byteLength > MAX_BYTES || thesisMutation.isPending;
  let t7;
  bb1: {
    if (tradeContext === null) {
      let t8;
      if ($[10] !== t) {
        t8 = t("suggestedPlaceholder.fallback");
        $[10] = t;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      t7 = t8;
      break bb1;
    }
    if (resolvedOutcome.label === null || resolvedOutcome.label.length === 0) {
      let t8;
      if ($[12] !== t) {
        t8 = t("suggestedPlaceholder.fallback");
        $[12] = t;
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      t7 = t8;
      break bb1;
    }
    const isBuy = tradeContext.direction === "buy";
    t7 = isBuy ? t("suggestedPlaceholder.buy", {
      outcome: resolvedOutcome.label
    }) : t("suggestedPlaceholder.sell", {
      outcome: resolvedOutcome.label
    });
  }
  const placeholder = t7;
  let t8;
  if ($[14] !== closeDialog) {
    t8 = function handleOpenChange(nextOpen) {
      if (!nextOpen) {
        closeDialog();
        setContent("");
        setView("input");
        setSubmittedSignature(null);
      }
    };
    $[14] = closeDialog;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[16] !== activeWallet || $[17] !== content || $[18] !== marketAddress || $[19] !== meQuery || $[20] !== onChainSignature || $[21] !== queryClient || $[22] !== resolvedOutcome || $[23] !== thesisMutation || $[24] !== tradeContext?.amountD6 || $[25] !== tradeContext?.direction || $[26] !== tradeContext?.outcomeIndex || $[27] !== tradeSignature) {
    t9 = function handleSubmit() {
      if (marketAddress === null || tradeSignature === null || activeWallet === null) {
        return;
      }
      const resolvedSignature = onChainSignature ?? tradeSignature;
      thesisMutation.mutate({
        signature: resolvedSignature,
        market: marketAddress,
        outcomeIndex: tradeContext?.outcomeIndex ?? 0,
        content
      }, {
        onSuccess: () => {
          useLocalThesesStore.getState().addLocalThesis(resolvedSignature, content);
          const me = meQuery.data;
          prependOptimisticThesis(queryClient, marketAddress, {
            signature: resolvedSignature,
            marketPubkey: marketAddress,
            outcomeIndex: tradeContext?.outcomeIndex ?? 0,
            outcomeLabel: resolvedOutcome.label ?? "",
            outcomeImageUrl: "",
            outcomeImageNsfw: true,
            content,
            createdAt: new Date().toISOString(),
            rating: 0,
            replyCount: 0,
            viewerReaction: null,
            tradeDirection: tradeContext?.direction ?? "buy",
            tradeAmount: tradeContext?.amountD6 ?? "0",
            trader: {
              userId: me?.id ?? null,
              walletAddress: activeWallet?.address ?? "",
              displayName: me?.displayName ?? null,
              handle: me?.handle ?? null,
              avatarUrl: me?.avatarUrl ?? null,
              avatarNsfw: me?.avatarNsfw ?? false,
              ...(me?.avatarPreferences !== undefined ? {
                avatarPreferences: me.avatarPreferences
              } : {}),
              connectedProviders: []
            },
            marketTitle: "",
            marketCategory: "",
            marketStatus: 0
          });
          setSubmittedSignature(resolvedSignature);
          setView("success");
          setContent("");
        }
      });
    };
    $[16] = activeWallet;
    $[17] = content;
    $[18] = marketAddress;
    $[19] = meQuery;
    $[20] = onChainSignature;
    $[21] = queryClient;
    $[22] = resolvedOutcome;
    $[23] = thesisMutation;
    $[24] = tradeContext?.amountD6;
    $[25] = tradeContext?.direction;
    $[26] = tradeContext?.outcomeIndex;
    $[27] = tradeSignature;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handleSubmit = t9;
  const hasTradeSummary = view === "input" && tradeContext !== null && resolvedOutcome.label !== null && resolvedOutcome.label.length > 0;
  let t10;
  if ($[29] !== t || $[30] !== view) {
    t10 = view === "input" ? t("dialogTitle") : t("success.title");
    $[29] = t;
    $[30] = view;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== hasTradeSummary || $[33] !== marketAddress || $[34] !== resolvedOutcome || $[35] !== t || $[36] !== tradeContext || $[37] !== view) {
    t11 = view === "success" ? t("success.body") : hasTradeSummary && tradeContext !== null ? <ThesisTradeSummary marketPubkey={marketAddress} context={tradeContext} resolvedOutcome={resolvedOutcome} /> : undefined;
    $[32] = hasTradeSummary;
    $[33] = marketAddress;
    $[34] = resolvedOutcome;
    $[35] = t;
    $[36] = tradeContext;
    $[37] = view;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== handleSubmit || $[40] !== isDisabled || $[41] !== submittedSignature || $[42] !== t || $[43] !== thesisMutation || $[44] !== view) {
    t12 = view === "input" ? <div className="flex items-center justify-end"><AnimatedButton disabled={isDisabled} loading={thesisMutation.isPending} onClick={handleSubmit}>{thesisMutation.isPending ? t("submitting") : t("submit")}</AnimatedButton></div> : <div className="flex items-center justify-end"><ThesisShareButton signature={submittedSignature} /></div>;
    $[39] = handleSubmit;
    $[40] = isDisabled;
    $[41] = submittedSignature;
    $[42] = t;
    $[43] = thesisMutation;
    $[44] = view;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== byteLength || $[47] !== content || $[48] !== handleSubmit || $[49] !== isDisabled || $[50] !== placeholder || $[51] !== t || $[52] !== view) {
    t13 = view === "input" ? <div className="flex flex-col gap-1.5"><TextareaField id="thesis-content" aria-label={t("inputLabel")} value={content} onChange={e => setContent(e.target.value)} onKeyDown={e_0 => {
        if ((e_0.metaKey || e_0.ctrlKey) && e_0.key === "Enter" && !isDisabled) {
          e_0.preventDefault();
          handleSubmit();
        }
      }} placeholder={placeholder} className="min-h-52! resize-none" /><span className={cn("text-caption", byteLength > MAX_BYTES ? "text-feedback-error-content" : "text-foreground-secondary")}>{t("byteCount", {
          count: byteLength
        })}</span></div> : null;
    $[46] = byteLength;
    $[47] = content;
    $[48] = handleSubmit;
    $[49] = isDisabled;
    $[50] = placeholder;
    $[51] = t;
    $[52] = view;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== handleOpenChange || $[55] !== open || $[56] !== t10 || $[57] !== t11 || $[58] !== t12 || $[59] !== t13) {
    t14 = <SilkSheet open={open} onOpenChange={handleOpenChange} variant="detached" size="md" keyboard={true} title={t10} description={t11} footer={t12}>{t13}</SilkSheet>;
    $[54] = handleOpenChange;
    $[55] = open;
    $[56] = t10;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
  } else {
    t14 = $[60];
  }
  return t14;
}

// ── Trade summary ─────────────────────────────────────────────────────────

interface ThesisTradeSummaryProps {
  marketPubkey: string | null;
  context: ThesisTradeContext;
  resolvedOutcome: ResolvedOutcome;
}
function ThesisTradeSummary(t0) {
  const $ = _c(11);
  const {
    marketPubkey,
    context,
    resolvedOutcome
  } = t0;
  const t = useTranslations("thesis");
  const {
    formatD6
  } = useFormattedCurrency();
  const outcomeLabel = resolvedOutcome.label ?? "";
  const {
    hue
  } = useMarketOutcomeColor(marketPubkey, context.outcomeIndex);
  const isBuy = context.direction === "buy";
  let t1;
  if ($[0] !== hue) {
    t1 = chunks => <span className="font-medium" style={{
      color: hue === undefined ? undefined : outcomeHueToStepVar(hue, 400)
    }}>{chunks}</span>;
    $[0] = hue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const outcomeChunk = t1;
  let t2;
  if ($[2] !== context.amountD6 || $[3] !== formatD6 || $[4] !== isBuy || $[5] !== outcomeChunk || $[6] !== outcomeLabel || $[7] !== t) {
    const amount = formatD6(context.amountD6);
    t2 = isBuy ? t.rich("tradeSummary.buy", {
      outcomeLabel,
      amount,
      outcome: outcomeChunk
    }) : t.rich("tradeSummary.sell", {
      outcomeLabel,
      amount,
      outcome: outcomeChunk
    });
    $[2] = context.amountD6;
    $[3] = formatD6;
    $[4] = isBuy;
    $[5] = outcomeChunk;
    $[6] = outcomeLabel;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <>{t2}</>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

// ── Success footer ────────────────────────────────────────────────────────

/**
 * Footer action for the success view — the Share button only (the SilkSheet's
 * own X dismisses the sheet, so a separate "Done" button is redundant). Title +
 * description are rendered from the SilkSheet `title`/`description` props.
 */
function ThesisShareButton(t0) {
  const $ = _c(19);
  const {
    signature
  } = t0;
  const tShare = useTranslations("thesis.share");
  const {
    network
  } = useUserNetwork();
  if (signature === null || signature.length === 0) {
    return null;
  }
  let t1;
  bb0: {
    if (typeof window === "undefined") {
      t1 = "";
      break bb0;
    }
    let t2;
    if ($[0] !== network || $[1] !== signature) {
      const u = new URL(buildThesisShareUrl(signature), window.location.origin);
      withDemoMode(u, network === "mainnet" ? "mainnet" : "devnet");
      t2 = u.toString();
      $[0] = network;
      $[1] = signature;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const shareUrl = t1;
  let t2;
  if ($[3] !== signature) {
    t2 = {
      kind: "thesis",
      signature
    };
    $[3] = signature;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== tShare) {
    t3 = tShare("tweetText");
    $[5] = tShare;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== tShare) {
    t4 = tShare("modalTitle");
    $[7] = tShare;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== tShare) {
    t5 = tShare("button");
    $[9] = tShare;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <AnimatedButton prefix={LinkForwardIconSolid}>{t5}</AnimatedButton>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== shareUrl || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t6) {
    t7 = <ShareModal surface={t2} url={shareUrl} text={t3} title={t4} trigger={t6} />;
    $[13] = shareUrl;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}